import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";

import { cx } from "@/lib/cx";
import { TONE_SOFT, type Tone } from "./tones";
import { Button } from "./Button";

/**
 * A tag, a filter pill, a token in a field — one component for all of them.
 *
 * It exists because the *same tag* rendered three different ways within 100px
 * on the tasks screen: a blue chip in the capture hint, a grey pill in the
 * filter bar, and plain body text inside the task title. Four call sites had
 * four paddings, three radii and three tone rules, so `#design` did not look
 * like one thing that appears in several places — it looked like three
 * unrelated pieces of furniture.
 *
 * Two interactive shapes, decided by what you pass. Static status text belongs
 * to `ui/Badge`, so this component cannot grow a second status grammar:
 *
 * - **selectable** (`onSelect`) — a `Button`. Selected is a *solid* fill with
 *   an inverted label, never a tint, and it never animates: a 150ms crossfade
 *   on a filter row renders two chips looking selected at once (P6).
 * - **dismissible** (`onDismiss`) — a `<span>` wrapping the label and a
 *   separate ✕ button, because a button inside a button is invalid HTML and
 *   the two targets need separate accessible names anyway.
 *
 * On a coarse pointer an interactive chip grows to the 44px floor. A filter
 * row is top-level navigation on the phone; a 24px pill is not a target.
 */

export type ChipSize = "sm" | "md";

/**
 * How a selectable chip announces itself.
 *
 * - `toggle` — a filter that can be on or off independently (`aria-pressed`).
 * - `radio` — one of a set where exactly one wins. A set of `aria-pressed`
 *   toggles is the wrong shape for single-select and reads to a screen reader
 *   as five unrelated switches. Wrap these in {@link ChipRadioGroup}.
 * - `action` — a one-shot command drawn as a chip: `＋ Add #design`. It has no
 *   pressed state at all, and saying `aria-pressed="false"` about it announces
 *   "toggle button, not pressed" for something that will never be pressed.
 */
export type ChipSelection = "toggle" | "radio" | "action";

type ChipActionProps =
  | { onSelect: () => void; onDismiss?: () => void }
  | { onSelect?: never; onDismiss: () => void };

export type ChipProps = {
  /** The chip's text. Pass the `#` yourself — this component adds nothing. */
  label: ReactNode;
  tone?: Tone;
  size?: ChipSize;
  selected?: boolean;
  /** Makes the chip a button. */
  selection?: ChipSelection;
  /**
   * A trailing marker that is not a count — `ACTIVE`, a lane dot, a warning
   * glyph. It sits *outside* the truncating label span, so a long name clips
   * and the marker survives; passing it inside `label` truncates the marker
   * first, which is the one thing it must never do.
   */
  suffix?: ReactNode;
  /** A trailing count, e.g. how many tasks carry this tag. */
  count?: number;
  /** Adds a ✕. The label stays clickable if `onSelect` is also given. */
  /** Accessible name for the ✕, e.g. `Remove #design`. */
  dismissLabel?: string;
  /** Overrides the accessible name of the chip itself. */
  "aria-label"?: string;
  title?: string;
  disabled?: boolean;
  className?: string;
} & ChipActionProps;

/**
 * Both footprints use the shared 13/500 control-label step. Their 32px and
 * 40px boxes fit the row recipe and interactive instances rise to 44px for a
 * coarse pointer.
 */
const SIZES: Record<ChipSize, string> = {
  sm: "h-8 gap-1 px-3 text-label",
  md: "h-10 gap-2 px-4 text-label",
};

/**
 * Only the interactive shapes get the coarse-pointer floor, and it is a
 * *minimum* rather than a height so it cannot fight the size's own `h-*`.
 */
const TAP = "pointer-coarse:min-h-tap";

const SHAPE = "busee-chip inline-flex max-w-full items-center rounded-control border border-ps1-line whitespace-nowrap select-none";

export function Chip({
  label,
  tone = "neutral",
  size = "md",
  selected = false,
  onSelect,
  selection = "toggle",
  suffix,
  count,
  onDismiss,
  dismissLabel,
  "aria-label": ariaLabel,
  title,
  disabled = false,
  className,
}: ChipProps) {
  // Selection is a channel change, not an intensity change: a solid fill with
  // an inverted label can never be confused with the hover tint next to it.
  // No transition — see the component note above.
  const skin = selected
    ? "border-ps1-gold bg-selected text-selected-fg"
    : TONE_SOFT[tone];

  const body = (
    <>
      <span className="min-w-0 truncate">{label}</span>
      {suffix === undefined ? null : <span className="shrink-0">{suffix}</span>}
      {count === undefined ? null : (
        <span
          className={cx(
            "numeric shrink-0 rounded-control px-2 text-caption tracking-normal",
            // Inverted rather than washed. This was `bg-selected-fg/25
            // text-selected-fg` — near-white text on 25% white over the sage
            // fill, which composites to 3.50:1 in light at 12px, i.e. a value
            // picked by eye inside a system that is otherwise solved. Swapping
            // the two reuses a pair that is already in the measured table:
            // `selected-fg on selected-bg` is 5.61:1 light / 8.67:1 dark, and
            // it holds whatever `--selected-*` is re-tuned to later.
            selected ? "bg-selected-fg text-selected" : "bg-fill-2 text-muted",
          )}
        >
          {count}
        </span>
      )}
    </>
  );

  if (onDismiss) {
    // Two targets, so the container is inert and each child owns its own name.
    return (
      <span className={cx(SHAPE, SIZES[size], "pr-1", skin, className)} title={title}>
        {onSelect ? (
          <Button
            variant="tonal-ghost"
            size="fit"
            onClick={onSelect}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-pressed={selection === "toggle" ? selected : undefined}
            aria-checked={selection === "radio" ? selected : undefined}
            role={selection === "radio" ? "radio" : undefined}
            className={cx(
              "min-w-0 truncate rounded-control disabled:pointer-events-none",
              TAP,
            )}
          >
            {label}
          </Button>
        ) : (
          <span className="min-w-0 truncate">{label}</span>
        )}
        <Button
          variant="tonal-ghost"
          size="sm"
          iconOnly
          onClick={onDismiss}
          disabled={disabled}
          aria-label={dismissLabel ?? "Remove"}
          className="ml-1 shrink-0"
        >
          <svg viewBox="0 0 12 12" aria-hidden className="size-4 stroke-current" fill="none">
            <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </Button>
      </span>
    );
  }

  if (onSelect) {
    return (
      <Button
        variant="tonal-ghost"
        size="fit"
        onClick={onSelect}
        disabled={disabled}
        title={title}
        aria-label={ariaLabel}
        role={selection === "radio" ? "radio" : undefined}
        aria-pressed={selection === "toggle" ? selected : undefined}
        aria-checked={selection === "radio" ? selected : undefined}
        className={cx(
          SHAPE,
          SIZES[size],
          TAP,
          skin,
          "disabled:pointer-events-none disabled:bg-fill-4 disabled:text-faint",
          className,
        )}
      >
        {body}
      </Button>
    );
  }

  // The public type requires one of the two actions. This is only a guard for
  // untyped JavaScript callers; static status content must use `Badge`.
  return null;
}

/* -------------------------------------------------------------------------- */

export type ChipRadioGroupProps = {
  /** Names the group. An unlabelled radiogroup is noise. */
  label: string;
  /** The chips. Every one that participates needs `selection="radio"`. */
  children: ReactNode;
  className?: string;
};

/**
 * The keyboard model a `role="radiogroup"` of chips is required to have.
 *
 * Three rows in the app declared the role and then left every chip at its
 * native `tabIndex` of 0 with no arrow handling — so Tab visited all eleven
 * projects, or every tag, and the arrow keys did nothing. `Segmented` and
 * `SetPips` implement the ARIA pattern correctly; these did not, which made the
 * app inconsistent with itself. This is that model, factored out.
 *
 * It works off the DOM rather than off a list of options, because the callers
 * render chips with several contents (a project with a blocked dot, a
 * "＋ New routine", an "All" pill) and flattening those into one option type
 * would cost more than it saves. Arrows move the cursor and select in the same
 * step, which is the radiogroup pattern and is what a filter row wants.
 */
export function ChipRadioGroup({ label, children, className }: ChipRadioGroupProps) {
  const ref = useRef<HTMLDivElement>(null);

  const radios = () =>
    Array.from(
      ref.current?.querySelectorAll<HTMLElement>('[role="radio"]:not([disabled])') ?? [],
    );

  // No dependency list on purpose: the checked chip moves on any render of the
  // owner, and there is nothing here to depend on — the source of truth is the
  // `aria-checked` the chips just rendered. It is a walk over a handful of
  // nodes, so running it every commit is cheaper than tracking it.
  useEffect(() => {
    const items = radios();
    if (items.length === 0) {
      return;
    }
    const checked = items.findIndex((item) => item.getAttribute("aria-checked") === "true");
    // Nothing checked still leaves exactly one tab stop, or the group would be
    // unreachable from the keyboard entirely.
    const stop = checked === -1 ? 0 : checked;
    items.forEach((item, index) => {
      item.tabIndex = index === stop ? 0 : -1;
    });
  });

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const { key } = event;
    const step = key === "ArrowRight" || key === "ArrowDown" ? 1 : key === "ArrowLeft" || key === "ArrowUp" ? -1 : 0;
    if (step === 0 && key !== "Home" && key !== "End") {
      return;
    }

    const items = radios();
    if (items.length === 0) {
      return;
    }

    const origin = (event.target as HTMLElement | null)?.closest<HTMLElement>('[role="radio"]');
    const from = origin ? items.indexOf(origin) : -1;
    if (from === -1 && step !== 0) {
      return;
    }

    event.preventDefault();
    const next =
      key === "Home"
        ? items[0]
        : key === "End"
          ? items[items.length - 1]
          : items[(from + step + items.length) % items.length];

    // Focus before click: the click re-renders the owner, and moving focus
    // afterwards would race that render.
    next?.focus();
    next?.click();
  }

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cx("flex flex-wrap items-center gap-2", className)}
    >
      {children}
    </div>
  );
}
